import { Subject, Observable, BehaviorSubject } from "rxjs";
import { share } from "rxjs/operators";
import {
  WSMessage,
  Topic,
  ConnectionConfig,
  ConnectionState,
  WebSocketManagerConfig,
} from "./types";
import { WebSocketConnection } from "./WebSocketConnection";

class WebSocketManager {
  private connections: Map<string, WebSocketConnection> = new Map();
  private topicSubjects: Map<Topic, Subject<any>> = new Map();
  private topicConnections: Map<Topic, Set<string>> = new Map();
  private connectionTopics: Map<string, Set<Topic>> = new Map();
  private connected$ = new BehaviorSubject<boolean>(false);
  private topicSubscriberCount: Map<Topic, number> = new Map();

  private getToken: () => string | null = () => null;
  private buildUrl: (endpoint: string) => string = (e) => e;
  private configured = false;

  /**
   * Must be called once at app startup before any connections are opened.
   *
   * Example (cloud-ui):
   *   wsManager.configure({
   *     getToken: () => localStorage.getItem("pulsejwt"),
   *     buildUrl: (endpoint) => `${grab_base_url(window.location.origin, "wss")}${endpoint}`,
   *   });
   *
   * Example (Atlas):
   *   wsManager.configure({
   *     getToken: () => localStorage.getItem("atlasjwt"),
   *     buildUrl: (endpoint) => buildWsUrl(endpoint),
   *   });
   */
  configure(config: WebSocketManagerConfig) {
    this.getToken = config.getToken;
    this.buildUrl = config.buildUrl;
    this.configured = true;
  }

  connect(
    endpoint: string,
    config: Partial<ConnectionConfig> = {}
  ): Observable<boolean> {
    if (!this.configured) {
      console.warn(
        "[WSManager] wsManager.configure() has not been called. Call it once at app startup."
      );
    }

    const connectionId = endpoint;

    if (this.connections.has(connectionId)) {
      const conn = this.connections.get(connectionId)!;
      if (!conn.isConnected()) {
        conn.connect();
      }
      return this.connected$.asObservable();
    }

    const connection = new WebSocketConnection(
      connectionId,
      endpoint,
      config,
      (msg, connId) => this.handleMessage(msg, connId),
      this.getToken,
      this.buildUrl
    );

    this.connections.set(connectionId, connection);

    connection.getState().subscribe(() => {
      const hasAnyConnection = Array.from(this.connections.values()).some((c) =>
        c.isConnected()
      );
      this.connected$.next(hasAnyConnection);
    });

    connection.connect();

    return this.connected$.asObservable();
  }

  subscribe<T = any>(topic: Topic, endpoint?: string): Observable<T> {
    if (!this.topicSubjects.has(topic)) {
      this.topicSubjects.set(topic, new Subject<T>());
      this.topicSubscriberCount.set(topic, 0);
    }

    const count = this.topicSubscriberCount.get(topic) || 0;
    this.topicSubscriberCount.set(topic, count + 1);

    if (endpoint) {
      this.ensureConnectionForTopic(endpoint, topic);
    }

    return this.topicSubjects.get(topic)!.asObservable().pipe(share()) as Observable<T>;
  }

  unsubscribe(topic: Topic) {
    const count = (this.topicSubscriberCount.get(topic) || 1) - 1;
    this.topicSubscriberCount.set(topic, count);

    if (count <= 0) {
      const subject = this.topicSubjects.get(topic);
      if (subject) {
        subject.complete();
        this.topicSubjects.delete(topic);
        this.topicSubscriberCount.delete(topic);
      }
      this.cleanupUnusedConnections(topic);
    }
  }

  send(endpoint: string, message: any): boolean {
    const connection = this.connections.get(endpoint);
    if (connection) {
      connection.send(message);
      return true;
    }
    console.warn(`[WSManager] No connection found for endpoint: ${endpoint}`);
    return false;
  }

  disconnect(endpoint: string) {
    const connection = this.connections.get(endpoint);
    if (connection) {
      connection.disconnect();
      this.connections.delete(endpoint);

      const topics = this.connectionTopics.get(endpoint);
      if (topics) {
        topics.forEach((topic) => {
          const conns = this.topicConnections.get(topic);
          if (conns) {
            conns.delete(endpoint);
            if (conns.size === 0) {
              this.topicConnections.delete(topic);
            }
          }
        });
        this.connectionTopics.delete(endpoint);
      }
    }
  }

  isConnected(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  getConnectionState(endpoint: string): Observable<ConnectionState> | null {
    return this.connections.get(endpoint)?.getState() ?? null;
  }

  private handleMessage(msg: any, connectionId: string) {
    const topic = this.resolveTopic(msg, connectionId);
    const wsMessage: WSMessage = {
      topic,
      payload: msg,
      source: connectionId,
      timestamp: Date.now(),
    };

    const subject = this.topicSubjects.get(topic);
    if (subject) {
      subject.next(wsMessage.payload);
    }
  }

  private resolveTopic(msg: any, connectionId: string): Topic {
    if (connectionId.includes("order_event")) return "order_event";

    if (connectionId.includes("/book/")) {
      const match = connectionId.match(/\/book\/([^?]+)/);
      return match ? (`orderbook.${match[1]}` as Topic) : "orderbook";
    }

    if (connectionId.includes("balance") || connectionId.includes("/portfolio/v1/asset")) return "balance_update";
    if (connectionId.includes("position")) return "position_update";

    if (
      connectionId.includes("/quoting/") ||
      connectionId.includes("/quote/")
    ) {
      if (connectionId.includes("market_data")) return "request_for_quote";
      if (connectionId.includes("order_entry")) return "quoting_order_entry";
      return "request_for_quote";
    }

    if (msg.event_type) return msg.event_type.toLowerCase() as Topic;
    if (connectionId.includes("trade")) return "trade_event";
    if (connectionId.includes("transfer")) return "transfer_event";

    return connectionId as Topic;
  }

  private ensureConnectionForTopic(connectionId: string, topic: Topic) {
    if (!this.topicConnections.has(topic)) {
      this.topicConnections.set(topic, new Set());
    }
    this.topicConnections.get(topic)!.add(connectionId);

    if (!this.connectionTopics.has(connectionId)) {
      this.connectionTopics.set(connectionId, new Set());
    }
    this.connectionTopics.get(connectionId)!.add(topic);
  }

  private cleanupUnusedConnections(unsubscribedTopic: Topic) {
    const connections = this.topicConnections.get(unsubscribedTopic);
    if (!connections) return;

    connections.forEach((connectionId) => {
      const topics = this.connectionTopics.get(connectionId);
      if (!topics) return;

      const hasActiveSubscribers = Array.from(topics).some(
        (topic) => (this.topicSubscriberCount.get(topic) || 0) > 0
      );

      if (!hasActiveSubscribers) {
        const connection = this.connections.get(connectionId);
        if (connection) {
          connection.disconnect();
          this.connections.delete(connectionId);
          this.connectionTopics.delete(connectionId);
        }
      }
    });
  }
}

export { WebSocketManager };
export const wsManager = new WebSocketManager();
